import { useState, useEffect } from 'react';

interface ProgressData {
  [courseId: string]: {
    [lessonId: string]: boolean;
  };
}

const STORAGE_KEY = 'math_lessons_progress';

export const useProgress = () => {
  const [progress, setProgress] = useState<ProgressData>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  }, [progress]);

  const markComplete = (courseId: string, lessonId: string) => {
    setProgress(prev => ({
      ...prev,
      [courseId]: {
        ...(prev[courseId] || {}),
        [lessonId]: true
      }
    }));
  };

  const markIncomplete = (courseId: string, lessonId: string) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      if (newProgress[courseId]) {
        const courseProgress = { ...newProgress[courseId] };
        delete courseProgress[lessonId];
        if (Object.keys(courseProgress).length === 0) {
          delete newProgress[courseId];
        } else {
          newProgress[courseId] = courseProgress;
        }
      }
      return newProgress;
    });
  };

  const toggleComplete = (courseId: string, lessonId: string) => {
    const isComplete = progress[courseId]?.[lessonId] || false;
    if (isComplete) {
      markIncomplete(courseId, lessonId);
    } else {
      markComplete(courseId, lessonId);
    }
  };

  const isComplete = (courseId: string, lessonId: string): boolean => {
    return progress[courseId]?.[lessonId] || false;
  };

  const getCourseProgress = (courseId: string, totalLessons: number): { completed: number; total: number; percentage: number } => {
    const courseProgress = progress[courseId] || {};
    const completed = Object.keys(courseProgress).length;
    const percentage = totalLessons > 0 ? Math.round((completed / totalLessons) * 100) : 0;
    return { completed, total: totalLessons, percentage };
  };

  const getTopicProgress = (courseId: string, lessonIds: string[]): { completed: number; total: number; percentage: number } => {
    const courseProgress = progress[courseId] || {};
    const completed = lessonIds.filter(id => courseProgress[id]).length;
    const total = lessonIds.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  };

  const resetCourse = (courseId: string) => {
    setProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[courseId];
      return newProgress;
    });
  };

  const resetAll = () => {
    setProgress({});
  };

  return {
    markComplete,
    markIncomplete,
    toggleComplete,
    isComplete,
    getCourseProgress,
    getTopicProgress,
    resetCourse,
    resetAll
  };
};